import { z } from 'zod';

import {
  INVESTMENT_PORTFOLIO_ROLES,
  INVESTMENT_REVIEW_CADENCES,
  INVESTMENT_THESIS_STATUSES,
} from '../enums';
import { AccountIdSchema } from './account';

const OptionalText = z.string().trim().min(1).max(4_000).nullable().optional();
const OptionalAllocation = z.number().min(0).max(1).nullable().optional();
const StringList = z.array(z.string().trim().min(1).max(240)).max(50).optional();

export const InvestmentThesisIdentitySchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('HL_CODE'),
    value: z.string().trim().min(1).max(64),
  }),
  z.object({
    source: z.literal('MANUAL'),
    value: z.string().trim().min(1).max(120),
    displaySymbol: z.string().trim().min(1).max(32),
  }),
]);

export const InvestmentThesisFieldsSchema = z.object({
  portfolioRole: z.enum(INVESTMENT_PORTFOLIO_ROLES).nullable().optional(),
  thesisSummary: OptionalText,
  targetAllocation: OptionalAllocation,
  maximumAllocation: OptionalAllocation,
  timeHorizon: z.string().trim().min(1).max(240).nullable().optional(),
  bullCase: OptionalText,
  baseCase: OptionalText,
  bearCase: OptionalText,
  thesisBreakers: StringList,
  keyKpis: StringList,
  valuationNotes: OptionalText,
  reviewCadence: z.enum(INVESTMENT_REVIEW_CADENCES).nullable().optional(),
  nextReviewAt: z.string().date().nullable().optional(),
});

function addAllocationIssue(
  value: {
    targetAllocation?: number | null | undefined;
    maximumAllocation?: number | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (
    value.targetAllocation !== undefined &&
    value.targetAllocation !== null &&
    value.maximumAllocation !== undefined &&
    value.maximumAllocation !== null &&
    value.targetAllocation > value.maximumAllocation
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Target allocation must not exceed maximum allocation.',
      path: ['targetAllocation'],
    });
  }
}

export const CreateInvestmentThesisSchema = z
  .object({
    accountId: AccountIdSchema,
    identity: InvestmentThesisIdentitySchema,
    status: z.enum(['DRAFT', 'ACTIVE']).optional(),
  })
  .merge(InvestmentThesisFieldsSchema)
  .superRefine(addAllocationIssue);

export const UpdateInvestmentThesisSchema = InvestmentThesisFieldsSchema.extend({
  status: z.enum(INVESTMENT_THESIS_STATUSES).optional(),
}).superRefine((value, ctx) => {
  addAllocationIssue(value, ctx);
  if (Object.values(value).every((field) => field === undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'At least one field must be provided.' });
  }
});

export const RebindInvestmentThesisIdentitySchema = z.object({
  identity: InvestmentThesisIdentitySchema,
  confirmedSameInvestment: z.literal(true),
  reason: z.string().trim().min(10).max(500),
});

export const CreateInvestmentThesisSuccessorSchema =
  InvestmentThesisFieldsSchema.superRefine(addAllocationIssue);

export const InvestmentThesisListQuerySchema = z.object({
  accountId: AccountIdSchema,
  includeClosed: z.enum(['true', 'false']).optional(),
});

export const InvestmentHoldingsQuerySchema = z.object({ accountId: AccountIdSchema });

export const InvestmentThesisParamsSchema = z.object({ id: z.string().cuid() });

export const InvestmentThesisActivationSchema = z.object({
  thesisSummary: z.string().trim().min(1).max(4_000),
  portfolioRole: z.enum(INVESTMENT_PORTFOLIO_ROLES),
  timeHorizon: z.string().trim().min(1).max(240),
  thesisBreakers: z.array(z.string().trim().min(1).max(240)).min(1).max(50),
});

export type CreateInvestmentThesisInput = z.infer<typeof CreateInvestmentThesisSchema>;
export type UpdateInvestmentThesisInput = z.infer<typeof UpdateInvestmentThesisSchema>;
export type RebindInvestmentThesisIdentityInput = z.infer<
  typeof RebindInvestmentThesisIdentitySchema
>;
export type CreateInvestmentThesisSuccessorInput = z.infer<
  typeof CreateInvestmentThesisSuccessorSchema
>;
